import { useForm } from "react-hook-form";
import { Alert, Button, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../../Context/AppContext";
import { useBookEvent } from "../../api/mutations/useBookEvent";
import { useSendEventBookingConfirmation } from "../../api/mutations/useSendEventBookingConfirmation";
import { useQueryClient } from "@tanstack/react-query";
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGetBookedEvents } from "../../api/queries/useGetBookedEvents";
import { BookedEvents } from "../../types/Types";
import { useGetEvent } from "../../api/queries/useGetEvent";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { useTranslation } from "react-i18next";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import {
  canUserViewEvent,
  isMembershipAccessCheckPending,
} from "../../utils/eventMembershipVisibility";
import {
  eventEligibleForMemberTierDiscount,
  isPaidExecutiveMember,
} from "../../utils/eventMemberDiscount";
import {
  isEventAdminFreeForUser,
  isEventFreeForGroup,
  isEventListPriceFree,
} from "../../utils/eventFreeForGroups";
import {
  formatEventDateTimeLabel,
  getEventCardPhase,
  type EventCardPhase,
} from "../../utils/eventDateUtils";
import { ensureExplorerProfileSyncedForEventBooking } from "../../explorer/explorerProfileSync";
import {
  EVENT_OBJECTIVES_OPTIONS,
  EVENT_OTHER_SECTOR_VALUE,
  EVENT_SECTOR_OPTIONS,
  EVENT_STAGE_OPTIONS,
} from "../../i18n/eventRegistrationI18n";
import "bootstrap-icons/font/bootstrap-icons.css";

const EventSuccessModal = lazy(() => import("./EventSuccessModal"));

const EVENT_DATA_KEY = "event-data";
const CONFIRM_BOOKING_PATH = "/confirm-booking";
const EVENT_BOOKING_PATH = "/event-booking";

const EVENT_FALLBACK_IMAGES = [
  "/SMBF-26.jpg",
  "/SMBF-27.jpg",
  "/SMBF-28.jpg",
  "/SMBF-29.jpg",
  "/SMBF-30.jpg",
  "/SMBF-31.jpg",
  "/SMBF-32.jpg",
  "/SMBF-33.jpg",
  "/SMBF-34.jpg",
  "/SMBF-35.jpg",
] as const;

type FormValues = {
  stageOfBusiness: string;
  objectives: string;
  sectorsOfInterest: string[];
  consentToShare: boolean;
};

interface ExecutiveTicketToastProps {
  onClose: () => void;
}

const ExecutiveTicketToast = memo(function ExecutiveTicketToast({
  onClose,
}: ExecutiveTicketToastProps) {
  const { t } = useTranslation();
  return (
    <Alert
      variant="success"
      className="mb-4 rounded-4 shadow-lg d-flex flex-column flex-md-row justify-content-between align-items-center gap-2"
    >
      <div className="text-center text-md-left">
        {t("events.executiveToastMessage")}
      </div>
      <button
        type="button"
        className="btn-close"
        aria-label={t("common.close")}
        onClick={onClose}
      />
    </Alert>
  );
});
ExecutiveTicketToast.displayName = "ExecutiveTicketToast";

function EventQuestions() {
  const { t, i18n } = useTranslation();

  const sortedStageOptions = useMemo(
    () =>
      [...EVENT_STAGE_OPTIONS].sort((a, b) =>
        t(a.labelKey).localeCompare(t(b.labelKey), i18n.language),
      ),
    [t, i18n.language],
  );

  const sortedObjectivesOptions = useMemo(
    () =>
      [...EVENT_OBJECTIVES_OPTIONS].sort((a, b) =>
        t(a.labelKey).localeCompare(t(b.labelKey), i18n.language),
      ),
    [t, i18n.language],
  );

  const sortedSectorOptions = useMemo(() => {
    const withoutOther = EVENT_SECTOR_OPTIONS.filter(
      (o) => o.value !== EVENT_OTHER_SECTOR_VALUE,
    );
    const sorted = [...withoutOther].sort((a, b) =>
      t(a.labelKey).localeCompare(t(b.labelKey), i18n.language),
    );
    const other = EVENT_SECTOR_OPTIONS.find(
      (o) => o.value === EVENT_OTHER_SECTOR_VALUE,
    );
    return other ? [...sorted, other] : sorted;
  }, [t, i18n.language]);
  const { objectId, user } = useAppContext();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showTicketToast, setShowTicketToast] = useState(true);
  const eventBookingSubmittedRef = useRef(false);
  const { data: bookedEvents } = useGetBookedEvents({ id: objectId || "" });
  const { data, isError: isEventLoadError } = useGetEvent({
    eventId: eventId || "",
  });
  const { data: memberships } = useGetMemberships();

  const membershipAccessPending = useMemo(
    () =>
      isMembershipAccessCheckPending(
        data?.allowedMembershipIds,
        user?.user?.membershipName,
        memberships,
        user?.user?.hasPaid,
        user?.user?.stripeStatus,
      ),
    [
      data?.allowedMembershipIds,
      user?.user?.membershipName,
      memberships,
      user?.user?.hasPaid,
      user?.user?.stripeStatus,
    ],
  );

  const canAccessEvent = useMemo(() => {
    if (!data || membershipAccessPending) return true;
    return canUserViewEvent(
      data.allowedMembershipIds,
      user?.user?.membershipId,
      {
        membershipName: user?.user?.membershipName,
        memberships,
        hasPaid: user?.user?.hasPaid,
        stripeStatus: user?.user?.stripeStatus,
      },
    );
  }, [
    data,
    membershipAccessPending,
    user?.user?.membershipId,
    user?.user?.membershipName,
    user?.user?.hasPaid,
    user?.user?.stripeStatus,
    memberships,
  ]);

  useEffect(() => {
    if (isEventLoadError) {
      navigate(EVENT_BOOKING_PATH, { replace: true });
    }
  }, [isEventLoadError, navigate]);

  useEffect(() => {
    eventBookingSubmittedRef.current = false;
  }, [eventId]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const { mutate: sendBookingConfirmationMail } =
    useSendEventBookingConfirmation();

  const onBookSuccess = useCallback(
    (bookResult: unknown) => {
      queryClient.invalidateQueries({
        queryKey: ["booked-events", objectId],
      });
      localStorage.removeItem(EVENT_DATA_KEY);

      const toEmail = user?.user?.email ?? "";
      if (toEmail.trim()) {
        const fullName =
          `${user?.user?.firstName ?? ""} ${user?.user?.lastName ?? ""}`.trim() ||
          "Member";
        const eventName = data?.name || "SMBF Event";
        const eventDateText = data?.eventDate
          ? new Date(data.eventDate).toLocaleString()
          : "TBC";
        const parsedId = eventId ? parseInt(eventId, 10) : NaN;
        sendBookingConfirmationMail({
          ...(Number.isFinite(parsedId) && parsedId > 0
            ? { eventId: parsedId }
            : {}),
          email: toEmail,
          name: fullName,
          firstName: user?.user?.firstName ?? undefined,
          eventName,
          eventDateText,
        });
      }

      setShowSuccessModal(true);
    },
    [
      queryClient,
      objectId,
      user?.user?.email,
      user?.user?.firstName,
      user?.user?.lastName,
      data?.name,
      data?.eventDate,
      eventId,
      sendBookingConfirmationMail,
    ],
  );
  const { mutate: bookEvent } = useBookEvent({ onSuccess: onBookSuccess });

  /** Bookings that consume Executive first-free-in-month (mirrors API: exclude FreeForGroups Executive + list-price free). */
  const filteredEvents = useMemo(() => {
    const raw = bookedEvents as unknown;
    const list: BookedEvents[] = Array.isArray(raw)
      ? raw
      : raw
        ? [raw as BookedEvents]
        : [];

    return list.filter((event) => {
      if (!event?.eventDate || !data?.eventDate) return false;
      if (!eventEligibleForMemberTierDiscount(event.memberEventDiscountEligible))
        return false;
      if (isEventFreeForGroup(event.freeForGroups, "Executive")) return false;
      if (isEventListPriceFree(event.price)) return false;

      const eventDate = new Date(event.eventDate);
      const targetDate = new Date(data.eventDate);

      const eventMonth = eventDate.getMonth();
      const eventYear = eventDate.getFullYear();
      const targetMonth = targetDate.getMonth();
      const targetYear = targetDate.getFullYear();

      return eventMonth === targetMonth && eventYear === targetYear;
    });
  }, [bookedEvents, data?.eventDate]);

  const isExecutiveMember = useMemo(
    () => isPaidExecutiveMember(user?.user),
    [user?.user],
  );

  const memberDiscountEligible = eventEligibleForMemberTierDiscount(
    data?.memberEventDiscountEligible,
  );

  const listPriceFree = isEventListPriceFree(data?.price);

  const adminFreeForUser = useMemo(
    () => isEventAdminFreeForUser(data?.freeForGroups, user?.user),
    [data?.freeForGroups, user?.user],
  );

  const complimentaryTicketPath = useMemo(
    () =>
      listPriceFree ||
      adminFreeForUser ||
      (filteredEvents.length < 1 &&
        isExecutiveMember &&
        memberDiscountEligible),
    [
      listPriceFree,
      adminFreeForUser,
      filteredEvents.length,
      isExecutiveMember,
      memberDiscountEligible,
    ],
  );

  const shouldShowToast = useMemo(
    () =>
      showTicketToast &&
      !listPriceFree &&
      !adminFreeForUser &&
      filteredEvents.length < 1 &&
      isExecutiveMember &&
      memberDiscountEligible,
    [
      showTicketToast,
      listPriceFree,
      adminFreeForUser,
      filteredEvents.length,
      isExecutiveMember,
      memberDiscountEligible,
    ],
  );

  const onSubmit = useCallback(
    async (formData: FormValues) => {
      localStorage.setItem(EVENT_DATA_KEY, JSON.stringify(formData));
      if (!complimentaryTicketPath) {
        navigate(`${CONFIRM_BOOKING_PATH}/${eventId}`);
      } else {
        if (eventBookingSubmittedRef.current) return;
        eventBookingSubmittedRef.current = true;
        try {
          await ensureExplorerProfileSyncedForEventBooking();
        } catch {
          /* best-effort before AddEventAttendee merge */
        }
        bookEvent({
          stageOfBusiness: formData.stageOfBusiness,
          eventObjectives: formData.objectives,
          sectorsOfInterest: (formData.sectorsOfInterest ?? []).join(", "),
          consentToShareProfile: formData.consentToShare ? 1 : 0,
          eventId: eventId ? parseInt(eventId, 10) : 0,
          userId: objectId || "",
        });
      }
    },
    [
      complimentaryTicketPath,
      navigate,
      eventId,
      bookEvent,
      objectId,
    ],
  );

  const handleCloseToast = useCallback(() => {
    setShowTicketToast(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowSuccessModal(false);
  }, []);

  const dateLocale = i18n.resolvedLanguage || i18n.language;
  const dynamicFields = useMemo(
    () => ({
      name: data?.name ?? "",
      description: data?.description ?? "",
      location: data?.location ?? "",
    }),
    [data?.name, data?.description, data?.location],
  );
  const { data: translatedFields } = useTranslateDynamicFields(
    dynamicFields,
    dateLocale,
  );
  const tx = (key: keyof typeof dynamicFields, fallback: string) =>
    translatedFields?.[key]?.trim() || fallback;

  const eventPhase: EventCardPhase | null = data?.eventDate
    ? getEventCardPhase(data.eventDate)
    : null;
  const phaseLabel =
    eventPhase === "today"
      ? t("events.today")
      : eventPhase === "upcoming"
        ? t("events.upcoming")
        : eventPhase === "expired"
          ? t("events.ended")
          : "";
  const ribbonIcon =
    eventPhase === "today"
      ? "bi-calendar-event-fill"
      : eventPhase === "upcoming"
        ? "bi-clock-fill"
        : "bi-x-circle-fill";
  const ribbonTone =
    eventPhase === "today"
      ? "bg-success text-white"
      : eventPhase === "upcoming"
        ? "bg-project-gradient text-white"
        : "bg-danger text-white";
  const formattedEventDate = data?.eventDate
    ? formatEventDateTimeLabel(data.eventDate, dateLocale)
    : t("common.na");
  const eventImageUrl =
    data?.bannerImage ||
    EVENT_FALLBACK_IMAGES[(data?.id ?? 0) % EVENT_FALLBACK_IMAGES.length];
  const eventTitle = tx("name", data?.name ?? "");
  const eventDescription = tx("description", data?.description ?? "");
  const eventLocation =
    tx("location", data?.location ?? "") || t("common.na");
  const isOnlineEvent = !!data?.isOnline;

  if ((!canAccessEvent && !membershipAccessPending) || isEventLoadError) {
    return (
      <main className="container-fluid px-0 pb-4" aria-label="Event registration questions">
        <Alert variant="warning">{t("events.membershipNotAvailable")}</Alert>
      </main>
    );
  }

  return (
    <main
      className="container-fluid px-0 pb-4"
      aria-label={t("events.registrationQuestionsAria")}
    >
      {shouldShowToast && <ExecutiveTicketToast onClose={handleCloseToast} />}

      <div className="card shadow-lg rounded-4 border-0">
        <Form onSubmit={handleSubmit(onSubmit)} className="p-3 p-md-4">
          {data ? (
            <div className="border rounded-3 p-3 mb-4 bg-white">
              <div className="d-flex flex-column flex-sm-row gap-3">
                <div
                  className="position-relative flex-shrink-0 rounded-3 overflow-hidden"
                  style={{ width: 120, maxWidth: "100%" }}
                >
                  <div className="ratio ratio-4x3">
                    <img
                      src={eventImageUrl}
                      alt={eventTitle}
                      className="object-fit-cover w-100 h-100"
                    />
                  </div>
                  {eventPhase && phaseLabel ? (
                    <div
                      className="position-absolute top-0 end-0 overflow-hidden"
                      style={{
                        width: 64,
                        height: 64,
                        pointerEvents: "none",
                      }}
                      aria-label={phaseLabel}
                    >
                      <span
                        className={`position-absolute ${ribbonTone} small fw-semibold text-uppercase d-inline-flex align-items-center justify-content-center gap-1 shadow-sm`}
                        style={{
                          width: 96,
                          top: 12,
                          right: -26,
                          transform: "rotate(45deg)",
                          padding: "0.15rem 0",
                          fontSize: "0.55rem",
                          letterSpacing: "0.02em",
                        }}
                      >
                        <i className={`bi ${ribbonIcon}`} aria-hidden />
                        {phaseLabel}
                      </span>
                    </div>
                  ) : null}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <h3 className="h6 fw-bold mb-1">{eventTitle}</h3>
                  {eventDescription ? (
                    <p
                      className="text-muted mb-2"
                      style={{ fontSize: "13px" }}
                    >
                      {eventDescription}
                    </p>
                  ) : null}
                  <div
                    className="d-flex flex-wrap align-items-center gap-3 text-muted"
                    style={{ fontSize: "13px" }}
                  >
                    <span className="d-inline-flex align-items-center gap-1">
                      <i className="bi bi-calendar3" aria-hidden />
                      {formattedEventDate}
                    </span>
                    {isOnlineEvent ? (
                      <span className="d-inline-flex align-items-center gap-1 min-w-0">
                        <i
                          className="bi bi-camera-video flex-shrink-0"
                          aria-hidden
                        />
                        <span className="text-truncate">
                          {t("events.onlineEvent")}
                        </span>
                      </span>
                    ) : (
                      <span className="d-inline-flex align-items-center gap-1 min-w-0">
                        <i className="bi bi-geo-alt flex-shrink-0" aria-hidden />
                        <span className="text-truncate">{eventLocation}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <Form.Group controlId="stageOfBusiness" className="mb-3">
            <Form.Label className="fw-semibold">
              {t("events.stageOfBusiness")} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              {...register("stageOfBusiness", {
                required: t("events.thisFieldRequired"),
              })}
            >
              <option value="">{t("explorer.segmentation.select")}</option>
              {sortedStageOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </Form.Select>
            {errors.stageOfBusiness && (
              <Form.Text className="text-danger">
                {errors.stageOfBusiness.message}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="objectives" className="mb-3">
            <Form.Label className="fw-semibold">
              {t("events.objectivesQuestion")}{" "}
              <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              {...register("objectives", {
                required: t("events.thisFieldRequired"),
              })}
            >
              <option value="">{t("explorer.segmentation.select")}</option>
              {sortedObjectivesOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </Form.Select>
            {errors.objectives && (
              <Form.Text className="text-danger">
                {errors.objectives.message}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              {t("events.sectorsOfInterest")}{" "}
              <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex flex-column gap-2">
              {sortedSectorOptions.map((sector, index) => {
                const fieldId = `sector-${index}`;
                return (
                  <Form.Check
                    key={sector.value}
                    id={fieldId}
                    className="mb-0"
                    type="checkbox"
                    value={sector.value}
                    label={t(sector.labelKey)}
                    {...register("sectorsOfInterest", {
                      required: t("events.atLeastOneSector"),
                      validate: (value) =>
                        (Array.isArray(value) && value.length > 0) ||
                        t("events.atLeastOneSector"),
                    })}
                  />
                );
              })}
            </div>
            {errors.sectorsOfInterest && (
              <Form.Text className="text-danger">
                {errors.sectorsOfInterest.message}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              {t("events.consentQuestion")}{" "}
              <span className="text-danger">*</span>
            </Form.Label>
            <Form.Check
              id="consentToShare"
              type="checkbox"
              label={t("events.yesConsent")}
              {...register("consentToShare", {
                required: t("events.consentRequired"),
              })}
            />
            {errors.consentToShare && (
              <Form.Text className="text-danger">
                {errors.consentToShare.message}
              </Form.Text>
            )}
          </Form.Group>

          <div className="d-flex justify-content-end mt-5">
            <Button className="px-4" type="submit">
              {t("common.post")}
            </Button>
          </div>
        </Form>
      </div>
      {showSuccessModal && (
        <Suspense fallback={null}>
          <EventSuccessModal
            show={showSuccessModal}
            onHide={handleCloseModal}
            text={t("events.bookingConfirmed")}
            meetingLink={
              data?.isOnline ? data?.meetingLink : null
            }
          />
        </Suspense>
      )}
    </main>
  );
}

EventQuestions.displayName = "EventQuestions";

export default memo(EventQuestions);
